import { createDriver as createAxiosDriver } from '@redux-requests/axios';
import {
  abortRequests,
  Driver,
  getQuery,
  handleRequests,
} from '@redux-requests/core';
import { createDriver } from '@redux-requests/promise';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import axios from 'axios';
import { connectRouter, routerMiddleware } from 'connected-react-router';
import { i18nSlice } from 'modules/i18n/i18nSlice';
import { LAYOUT_STATE_NAME, layoutReducer } from 'modules/layout/store/layout';
import { persistReducer, persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';
import { setAccount } from '../modules/account/store/actions/setAccount';
import { extractMessage } from '../modules/common/utils/extractError';
import { historyInstance } from '../modules/common/utils/historyInstance';
import { NotificationActions } from '../modules/notification/store/NotificationActions';
import { notificationSlice } from '../modules/notification/store/notificationSlice';
import { rootSaga } from './rootSaga';
import { i18nPersistConfig } from './webStorageConfigs';
import {
  BlockchainNetworkId,
} from '../modules/common/conts';
import { disconnect } from 'modules/account/store/actions/disconnect';
import { likeSlice } from 'modules/common/store/like';
import { getChainId } from 'modules/common/utils/localStorage';
import { userSlice } from 'modules/common/store/user';
import { getNotWeb3WalletInfo } from 'modules/account/hooks/useWeb3React';

type MainApiDriverName =
  | 'mainApiEthMainnet';

const chainToMainApiDriver: {
  [key in BlockchainNetworkId]: MainApiDriverName | undefined;
} = {
  [BlockchainNetworkId.mainnet]: 'mainApiEthMainnet',
  [BlockchainNetworkId.sepolia]: undefined,
};

function getMainApiDriverName(chainId: BlockchainNetworkId): MainApiDriverName {
  return chainToMainApiDriver[chainId] || 'mainApiEthMainnet';
}

type NftViewApiDriverName =
  | 'nftViewApiEthMainnet';

type NftView2ApiDriverName =
  | 'nftView2ApiEthMainnet';


export type DriverName =
  | MainApiDriverName
  | NftViewApiDriverName
  | NftView2ApiDriverName;

const mainAxios = {
  mainApiEthMainnet: axios.create({
    baseURL: process.env.REACT_APP_API_BASE,
  }),
};

type mainApiDriversType = keyof typeof mainAxios;

const mainApiDrivers: { [key in mainApiDriversType]: Driver } = {
  ...mainAxios,
};
Object.keys(mainAxios).forEach(key => {
  const initAxios = mainAxios[key as mainApiDriversType];
  initAxios.interceptors.response.use(response => {
    // token invalid
    if ([-1, -2, 401].includes(response.data?.errorCode ?? response.data?.code)) {
      store.dispatch(abortRequests());
      store.dispatch(disconnect());
    }
    return response;
  });
  mainApiDrivers[key as mainApiDriversType] = createAxiosDriver(initAxios);
});

const { requestsReducer, requestsMiddleware } = handleRequests({
  driver: {
    default: createDriver({
      processResponse: response => ({ data: response, project: 'sp' }),
    }),
    ...mainApiDrivers,
    nftViewApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_FANGIBLE_URL_ETH_MAINNET,
      }),
    ),
    nftView2ApiEthMainnet: createAxiosDriver(
      axios.create({
        baseURL: process.env.REACT_APP_API_BASE,
      }),
    ),
  },
  onRequest: (request, action, store) => {
    const rootState: RootState = store.getState();

    const { data } = getQuery(rootState, {
      type: setAccount.toString(),
      action: setAccount,
    });

    const chainId =
      data?.chainId ?? getChainId() ?? getNotWeb3WalletInfo()?.chainId;

    // console.log('chainId', chainId);

    if (action.meta?.driver === 'axios') {
      action.meta = {
        ...action.meta,
        driver: getMainApiDriverName(chainId),
      };
    }
    // TODO Throw exception if auth and no token?

    if (action.meta?.auth) {
      return {
        ...request,
        headers: {
          ...request.headers,
          signature: data?.token ?? '',
        },
      };
    }

    return {
      ...request,
      headers: {
        ...request.headers,
        project: 'sp',
      },
    };
  },
  onError: (error, action, store) => {
    console.log("store", JSON.stringify(store))
    console.log("error", JSON.stringify(error))
    if (!action.meta?.suppressErrorNotification && (extractMessage(error) !== "Error: data not found")) {
      store.dispatch(
        NotificationActions.showNotification({
          message: extractMessage(error),
          severity: 'error',
        }),
      );
    }

    throw error;
  },
});

const sagaMiddleware = createSagaMiddleware();

const rootReducer = combineReducers({
  i18n: persistReducer(i18nPersistConfig, i18nSlice.reducer),
  requests: requestsReducer,
  router: connectRouter(historyInstance),
  notifications: notificationSlice.reducer,
  [LAYOUT_STATE_NAME]: layoutReducer,
  like: likeSlice.reducer,
  user: userSlice.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: [
    ...requestsMiddleware,
    routerMiddleware(historyInstance),
    sagaMiddleware,
  ],
});

export const persistor = persistStore(store);

sagaMiddleware.run(rootSaga);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
